import React, { useEffect, useState } from 'react';
import { Loader2, Save, VolumeX } from 'lucide-react';

import { callingBehaviorConfigAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from '../ui/use-toast';

// 2026-10-04: background-noise handling on WhatsApp calls. Stored in the
// account's calling settings document (AccountCallingBehaviorConfig.settings,
// defaults in backend app/config/calling_defaults.py). Saves ONLY these keys
// -- the backend merges partial settings, so the Call Behavior card's own
// settings are untouched.

type SpeakerMode = 'off' | 'log_only' | 'enforce';

type NoiseSettings = {
  vad_barge_in_enabled: boolean;
  denoise_before_stt_enabled: boolean;
  denoise_strength_db: number;
  // 2026-10-04 (Docs/CALLING_BACKGROUND_SPEAKER_REJECTION_PLAN.md §0)
  speaker_check_mode: SpeakerMode;
  speaker_similarity_threshold: number;
};

const SPEAKER_MODES: { value: SpeakerMode; label: string; help: string }[] = [
  { value: 'off', label: 'Off', help: 'No voice check.' },
  {
    value: 'log_only',
    label: 'Log only',
    help: 'Scores every turn in the backend log, but answers everyone. Use this first to see real scores.',
  },
  {
    value: 'enforce',
    label: 'On',
    help: "Turns that don't sound like the caller aren't answered; after 2 in a row the AI asks them to repeat.",
  },
];

const STRENGTHS = [
  { value: 8, label: 'Light' },
  { value: 12, label: 'Medium' },
  { value: 20, label: 'Strong' },
];

function fromApi(data: any): NoiseSettings {
  const effective = data?.effective_settings || {};
  return {
    vad_barge_in_enabled: !!effective.vad_barge_in_enabled,
    denoise_before_stt_enabled: !!effective.denoise_before_stt_enabled,
    denoise_strength_db: Number(effective.denoise_strength_db) || 12,
    speaker_check_mode: (['off', 'log_only', 'enforce'].includes(effective.speaker_check_mode)
      ? effective.speaker_check_mode
      : 'log_only') as SpeakerMode,
    speaker_similarity_threshold: Number(effective.speaker_similarity_threshold) || 0.5,
  };
}

function errorText(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
  return err?.message || 'Failed to save';
}

export default function CallingNoiseSettings() {
  const [settings, setSettings] = useState<NoiseSettings>({
    vad_barge_in_enabled: false,
    denoise_before_stt_enabled: false,
    denoise_strength_db: 12,
    speaker_check_mode: 'log_only',
    speaker_similarity_threshold: 0.5,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    callingBehaviorConfigAPI
      .getConfig()
      .then((data: any) => {
        if (!cancelled) setSettings(fromApi(data));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await callingBehaviorConfigAPI.updateConfig({ settings });
      setSettings(fromApi(updated));
      toast({ title: 'Saved', description: 'Background noise settings updated (applies to new calls).' });
    } catch (err: any) {
      toast({ title: 'Save failed', description: errorText(err), variant: 'destructive' as any });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const strengthKnown = STRENGTHS.some((s) => s.value === settings.denoise_strength_db);

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <VolumeX className="h-4 w-4" />
          Background noise
        </CardTitle>
        <CardDescription>
          For callers in noisy places. Changes apply to new calls.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Only real speech can interrupt the AI</p>
            <p className="text-xs text-muted-foreground">
              A TV, music or traffic won't cut the AI off mid-sentence — only someone talking will.
              No extra delay on answers.
            </p>
          </div>
          <Switch
            checked={settings.vad_barge_in_enabled}
            onCheckedChange={(on) => setSettings((s) => ({ ...s, vad_barge_in_enabled: on }))}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Clean up noise before transcribing</p>
            <p className="text-xs text-muted-foreground">
              Reduces hum and background noise in the caller's audio so it's transcribed more
              accurately. Adds about 0.1s per turn. Not applied to Deepgram streaming.
            </p>
          </div>
          <Switch
            checked={settings.denoise_before_stt_enabled}
            onCheckedChange={(on) => setSettings((s) => ({ ...s, denoise_before_stt_enabled: on }))}
          />
        </div>

        {settings.denoise_before_stt_enabled && (
          <div className="flex items-center gap-3 pl-1">
            <span className="text-xs text-muted-foreground">Strength</span>
            <Select
              value={String(settings.denoise_strength_db)}
              onValueChange={(v) => setSettings((s) => ({ ...s, denoise_strength_db: Number(v) }))}
            >
              <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STRENGTHS.map((s) => (
                  <SelectItem key={s.value} value={String(s.value)}>{s.label}</SelectItem>
                ))}
                {!strengthKnown && (
                  <SelectItem value={String(settings.denoise_strength_db)}>
                    Custom ({settings.denoise_strength_db} dB)
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">Stronger removes more noise but can thin out soft voices.</span>
          </div>
        )}

        <div className="space-y-2 border-t border-border/60 pt-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Ignore other people talking</p>
              <p className="text-xs text-muted-foreground">
                Learns the caller's voice from their first answers, then checks each turn is still them —
                so someone else in the room isn't answered.{' '}
                {SPEAKER_MODES.find((m) => m.value === settings.speaker_check_mode)?.help}
              </p>
            </div>
            <Select
              value={settings.speaker_check_mode}
              onValueChange={(v) => setSettings((s) => ({ ...s, speaker_check_mode: v as SpeakerMode }))}
            >
              <SelectTrigger className="h-8 w-32 shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SPEAKER_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {settings.speaker_check_mode !== 'off' && (
            <div className="flex items-center gap-3 pl-1">
              <span className="text-xs text-muted-foreground">Match threshold</span>
              <input
                type="range"
                min={0.1}
                max={0.95}
                step={0.05}
                value={settings.speaker_similarity_threshold}
                onChange={(e) => setSettings((s) => ({ ...s, speaker_similarity_threshold: Number(e.target.value) }))}
                className="w-40"
              />
              <span className="w-10 text-xs font-medium">{settings.speaker_similarity_threshold.toFixed(2)}</span>
              <span className="text-xs text-muted-foreground">
                Below this score = someone else. Tune from the "similarity=" values in the log.
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-border/60 pt-4">
          <Button onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
